import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getPaymentIntent } from '../apis/paymentIntent'
import {
	PAYMENT_CANCELED,
	STATE_AMEX_BLOCKED,
	STATE_CANCELED,
	STATE_FAILED,
	STATE_SUCCEEDED,
	PAYMENT_STATUS_TIMEOUT
} from '../constants'
import {
	getPracticePaymentIntentPollingIntervalMs,
	getPracticePaymentIntentPollingTimeoutMs
} from 'util/practice'
import {
	getConsolidatorPaymentIntentPollingIntervalMs,
	getConsolidatorPaymentIntentPollingTimeoutMs
} from 'util/consolidator'

export interface PaymentIntentStatusAwaiter {
	wait: () => Promise<string>
	cancel: () => void
}

export const usePaymentIntentStatusWatcher = (
	currentPracticeObject: IPractice,
	currentConsolidatorObject: IConsolidator | undefined
) => {
	const paymentIntentWatcherConfig = useMemo(
		() => ({
			pollingIntervalMs:
				getPracticePaymentIntentPollingIntervalMs(currentPracticeObject) ??
				getConsolidatorPaymentIntentPollingIntervalMs(currentConsolidatorObject),
			pollingTimeoutMs:
				getPracticePaymentIntentPollingTimeoutMs(currentPracticeObject) ??
				getConsolidatorPaymentIntentPollingTimeoutMs(currentConsolidatorObject)
		}),
		[currentPracticeObject, currentConsolidatorObject]
	)

	// Polling state
	const [pollingPaymentIntentId, setPollingPaymentIntentId] = useState<string | null>(null)
	const pollingResolveRef = useRef<(value: string) => void>()
	const pollingRejectRef = useRef<(reason?: any) => void>()
	const pollingTimeoutRef = useRef<number>()

	const stopPaymentIntentPolling = useCallback(() => {
		if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current)
		pollingTimeoutRef.current = undefined
		setPollingPaymentIntentId(null)
	}, [])

	const resolvePaymentIntentPolling = useCallback(
		(status: string) => {
			pollingResolveRef.current?.(status)
			stopPaymentIntentPolling()
		},
		[stopPaymentIntentPolling]
	)

	const rejectPaymentIntentPolling = useCallback(
		(error: any) => {
			pollingRejectRef.current?.(error)
			stopPaymentIntentPolling()
		},
		[stopPaymentIntentPolling]
	)

	// Cleanup polling on unmount
	useEffect(() => {
		return () => {
			stopPaymentIntentPolling()
		}
	}, [stopPaymentIntentPolling])

	const { data: paymentIntentData, error: paymentIntentError } = useQuery({
		queryKey: ['payment-intent-status', pollingPaymentIntentId],
		queryFn: async () => {
			const response = await getPaymentIntent(pollingPaymentIntentId as string)
			if (!response?.data?.status) {
				throw new Error('Invalid payment intent response: missing status')
			}
			return response?.data
		},
		enabled: !!pollingPaymentIntentId,
		refetchInterval: paymentIntentWatcherConfig.pollingIntervalMs,
		refetchOnWindowFocus: false,
		refetchOnReconnect: true,
		staleTime: 0,
		retry: (_failureCount, error: any) => {
			const status = error?.response?.status
			if (status === 401 || status === 403) return false
			return true
		},
		// Exponential backoff for retries
		retryDelay: (retryCount) =>
			Math.min(paymentIntentWatcherConfig.pollingIntervalMs, 100 * Math.pow(2, retryCount))
	})

	useEffect(() => {
		if (!paymentIntentData) return

		const { status } = paymentIntentData
		if (status === STATE_CANCELED && paymentIntentData.metadata?.amexCardBlocked === 'true') {
			resolvePaymentIntentPolling(STATE_AMEX_BLOCKED)
			return
		}

		if (status === STATE_SUCCEEDED) {
			resolvePaymentIntentPolling(STATE_SUCCEEDED)
			return
		}

		// A reader decline leaves the intent in requires_payment_method; only the charge shows it
		if (
			[STATE_FAILED, STATE_CANCELED].includes(status) ||
			paymentIntentData.latestChargeStatus === STATE_FAILED
		) {
			resolvePaymentIntentPolling(STATE_FAILED)
			return
		}
	}, [paymentIntentData, resolvePaymentIntentPolling])

	useEffect(() => {
		if (!paymentIntentError) return

		const status = paymentIntentError?.response?.status
		if (status === 401 || status === 403) {
			rejectPaymentIntentPolling(new Error('Authentication error while polling payment intent'))
		}
	}, [paymentIntentError, rejectPaymentIntentPolling])

	const waitForPaymentIntentStatus = useCallback(
		(paymentIntentId: string): PaymentIntentStatusAwaiter => {
			// Cancel any existing polling session
			if (pollingTimeoutRef.current) {
				clearTimeout(pollingTimeoutRef.current)
			}

			const paymentIntentPromise = new Promise<string>((resolve, reject) => {
				pollingResolveRef.current = resolve
				pollingRejectRef.current = reject
				setPollingPaymentIntentId(paymentIntentId)

				pollingTimeoutRef.current = window.setTimeout(() => {
					resolve(PAYMENT_STATUS_TIMEOUT)
					stopPaymentIntentPolling()
				}, paymentIntentWatcherConfig.pollingTimeoutMs)
			})

			return {
				wait: () => paymentIntentPromise,
				cancel: () => resolvePaymentIntentPolling(PAYMENT_CANCELED)
			}
		},
		[
			resolvePaymentIntentPolling,
			stopPaymentIntentPolling,
			paymentIntentWatcherConfig.pollingTimeoutMs
		]
	)

	return { waitForPaymentIntentStatus }
}
